import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import Checkbox from '~/components/inputs/Checkbox/Checkbox';
import { VStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalActions from '~/components/modals/Modal/ModalActions';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useAnalytics } from '~/hooks/useAnalytics';
import useDataTestId from '~/hooks/useDataTestId';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useUpdateCounterpart } from '~/services/counterparts';
import { useBulkUpdateFlows } from '~/services/flow';
import { Account } from '~/types/account';
import { Counterpart } from '~/types/counterparts';

export type EditFlowAccountDialogProps = ModalProps & {
  counterpart: Counterpart;
  accountId: Account['id'];
  dataTestId?: string;
};

const EditFlowAccountDialog = (props: EditFlowAccountDialogProps) => {
  const { dataTestId, counterpart, accountId } = props;
  const dt = useDataTestId(dataTestId);
  const { t } = useTranslation('flows');
  const [applyToHistory, setApplyToHistory] = useState(true);
  const { track } = useAnalytics();

  const { captureException } = useExceptionLogger();
  const bulkUpdate = useBulkUpdateFlows();
  const updateCounterpart = useUpdateCounterpart();

  const applyCounterpartAccountToHistory = async () => {
    try {
      await bulkUpdate.mutateAsync({
        payload: { accountId },
        filters: {
          counterpartId: counterpart.id,
          accountId: null,
        },
      });
      return true;
    } catch (err) {
      captureException(err, {
        extra: {
          counterpart_id: counterpart.id,
          account_id: accountId,
          context: '/outstanding',
          help_text: 'something goes wrong during the account bulk update based on counterpart',
        },
      });
      return false;
    }
  };

  const handleConfimClick = async () => {
    try {
      await updateCounterpart.mutateAsync({
        counterpart,
        data: {
          account: accountId,
        },
      });

      if (applyToHistory === false) {
        snack.success(t('edit_flow_account_dialog.success'));
      } else {
        const historyUpdated = await applyCounterpartAccountToHistory();
        if (historyUpdated) {
          snack.success(t('edit_flow_account_dialog.success'));
        } else {
          snack.warning(t('edit_flow_account_dialog.success_but_no_history_apply'));
        }
      }

      props.closeModal();
    } catch (error) {
      captureException(error, {
        extra: {
          counterpartIdentifier: counterpart.taxNumber,
          counterpartName: counterpart.companyName,
          counterpartId: counterpart.id,
          accountId,
          help_text: 'something goes wrong during the counterpart account id assignment',
        },
      });
      snack.error(t('edit_flow_account_dialog.failed'));
    } finally {
      track(AnalyticsEvents.PAYMENT_ACCOUNT_ASSIGNED_TO_COUNTERPART, {
        counterpartIdentifier: counterpart.taxNumber,
        counterpartName: counterpart.companyName,
        counterpartId: counterpart.id,
        paymentAccountId: accountId,
        applyToHistory,
      });
    }
  };

  return (
    <Modal isOpen maxWidth={600} data-testid={dt()}>
      <ModalTitle data-testid={dt('title')} onCloseClick={props.closeModal}>
        {t('edit_flow_account_dialog.title')}
      </ModalTitle>
      <ModalContent>
        <VStack gap={2}>
          <Text variant="body1" weight="regular">
            {t('edit_flow_account_dialog.text', { counterpart: counterpart.companyName })}
          </Text>
          <Checkbox checked={applyToHistory} onChange={setApplyToHistory}>
            {t('edit_flow_account_dialog.apply_to_history')}
          </Checkbox>
        </VStack>
      </ModalContent>
      <ModalActions>
        <Button variant="outlined" onClick={() => props.closeModal()} disabled={updateCounterpart.isPending}>
          {t('edit_flow_account_dialog.button_no')}
        </Button>
        <Button onClick={handleConfimClick} loading={updateCounterpart.isPending}>
          {t('edit_flow_account_dialog.button_yes')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default EditFlowAccountDialog;
